import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatGap, formatMoneyCompact, isNum } from '../format';
import { CORRECTIONS_CONTACT, pagePath } from '../siteConfig';
import { track } from '../../utils/track';
import GapChip from './GapChip';

export function NearbySuburbs({ items, from }) {
  if (!items?.length) return null;
  return (
    <ul className="re-nearby">
      {items.map((s) => (
        <li key={s.slug}>
          <Link
            to={pagePath('suburb', s.slug)}
            className="re-nearby__card"
            onClick={() => track('nearby_click', { from, to: s.slug })}
          >
            <span className="re-nearby__name">{s.name}</span>
            <span className="re-nearby__row">
              <GapChip value={s.guide_gap_pct} />
              <span className="re-nearby__price">{isNum(s.median_sale_price) ? `${formatMoneyCompact(s.median_sale_price)} median` : ''}</span>
            </span>
            {!s.indexable ? <span className="re-nearby__flag">Limited data</span> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Forecast is an index, not a median. Only shown when publishable (ADR-003). */
export function ForecastPanel({ forecast }) {
  if (!forecast) return null;
  const { low_pct: low, high_pct: high, change_pct: mid, horizon_months: months } = forecast;
  const lo = Math.min(low, 0) - 2;
  const hi = Math.max(high, 0) + 2;
  const at = (v) => `${(((v - lo) / (hi - lo)) * 100).toFixed(2)}%`;
  return (
    <div className="re-forecast">
      <div className="re-forecast__figure">
        <span className="re-forecast__value">{formatGap(mid)}</span>
        <span className="re-forecast__label">
          expected change in the {forecast.label} over {months} months
        </span>
      </div>
      <div className="re-forecast__range" aria-label={`Range ${formatGap(low)} to ${formatGap(high)}`}>
        <span className="re-forecast__rail" />
        <span className="re-forecast__band" style={{ left: at(low), width: `calc(${at(high)} - ${at(low)})` }} />
        <span className="re-forecast__zero" style={{ left: at(0) }} />
        <span className="re-forecast__mid" style={{ left: at(mid) }} />
        <span className="re-forecast__tick" style={{ left: at(low) }}>{formatGap(low)}</span>
        <span className="re-forecast__tick" style={{ left: at(high) }}>{formatGap(high)}</span>
      </div>
      <p className="re-footnote">
        A model estimate for comparable homes across the area, not a prediction for any one property, and not the same
        as the median sale price. <Link to={`/methodology#${forecast.methodology_anchor || 'forecast'}`}>How the forecast works</Link>
      </p>
    </div>
  );
}

/**
 * Email capture placeholder. The digest isn't live yet, so the button records
 * interest (a GA event) and says so plainly. No network call, no data stored.
 */
export function WatchCta({ name, slug, kind = 'suburb' }) {
  const [clicked, setClicked] = useState(false);
  return (
    <aside className="re-cta" aria-labelledby={`watch-${slug}`}>
      <div className="re-cta__copy">
        <h2 id={`watch-${slug}`}>Watch {name}</h2>
        <p>
          {kind === 'suburb'
            ? `A short weekly note when homes in ${name} sell, with every result set against its guide.`
            : `A note when ${name} lists or sells a property, with the result set against the guide.`}
        </p>
      </div>
      {clicked ? (
        <p className="re-cta__done" role="status">
          Weekly notes aren&rsquo;t live yet. Your click tells us to prioritise {name}.
        </p>
      ) : (
        <button
          type="button"
          className="re-button"
          onClick={() => {
            setClicked(true);
            track('watch_cta_click', { kind, slug });
          }}
        >
          Notify me
        </button>
      )}
    </aside>
  );
}

export function ClaimProfile({ name, slug }) {
  const [clicked, setClicked] = useState(false);
  return (
    <aside className="re-claim" aria-labelledby={`claim-${slug}`}>
      <h2 id={`claim-${slug}`}>Are you {name}?</h2>
      <p>
        Agents will be able to claim this profile to add context to their results, respond to the figures and get
        notified when new sales are added. Figures here are measurements from public listing and sales records. They
        say nothing about intent.
      </p>
      <div className="re-claim__actions">
        {clicked ? (
          <p className="re-cta__done" role="status">
            Profile claims open soon. We&rsquo;ve noted your interest.
          </p>
        ) : (
          <button
            type="button"
            className="re-button re-button--quiet"
            onClick={() => {
              setClicked(true);
              track('claim_profile_started', { slug });
            }}
          >
            I&rsquo;m this agent
          </button>
        )}
        <Link to="/methodology#corrections">Report an error</Link>
      </div>
      {CORRECTIONS_CONTACT?.email ? (
        <p className="re-footnote">Corrections: {CORRECTIONS_CONTACT.email}</p>
      ) : null}
    </aside>
  );
}

export function SeoFooter({ dataVersion }) {
  return (
    <footer className="re-seo-footer">
      <p>
        RealEstats compares Sydney sale results with the price guides homes were advertised at, using public listing
        and NSW sales records.
      </p>
      <nav aria-label="Data pages">
        <Link to="/suburbs">All suburbs</Link>
        <Link to="/leaderboard">Agent leaderboard</Link>
        <Link to="/methodology">Methodology</Link>
        <Link to="/methodology#corrections">Corrections</Link>
      </nav>
      {dataVersion ? <p className="re-seo-footer__version">Data version {dataVersion}</p> : null}
    </footer>
  );
}

export { GapChip };
